import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Icon, type IconName } from '../../components/icons';
import { Button, ChannelGlyph, Chip, StatusBadge, type DataColumn, type Status } from '../../components/ui';
import { RowMenu, type MenuAction } from '../../components/ui/menu';
import type { SwipeAction } from '../../components/ui/swipeRow';
import i18n from '../../i18n';
import type { PostType, ScheduledPost } from '../../lib/api';
import { formatNumber, formatScheduled, formatShortDate, formatWhen } from '../../lib/format';
import { composeCaption, platformOf } from './limits';
import { MediaThumb } from './MediaPanel';
import { InsightsNotice } from './PostInsights';

/**
 * The Posts and Stories lists' columns (redesign R12–R15, R20–R23, one table
 * for both kinds and all four tabs): the item, its status, then the column
 * that tab needs — when it goes live, when it was last edited, why it did not
 * go out — or, once published, its numbers. `DataTable` lays them out.
 *
 * A post and its copy on a Facebook Page (D-143) share a row while they share
 * a tab: the copy shows as "Aussi sur <Page>", its problems under the post's,
 * and the numbers are the two platforms' added together.
 */

export const TYPE_ICONS: Record<PostType, IconName> = { image: 'image', carousel: 'carousel', reel: 'reel', story: 'story' };

/** Which list a row sits in (D-137): posts (image, carousel, Reel) or stories. */
export type ListKind = 'post' | 'story';

export type Tab = 'upcoming' | 'drafts' | 'published' | 'failed';

/** At most this many automation chips on a row; the rest read "+2". */
const AUTOMATION_CHIPS = 2;

export const captionOf = (post: ScheduledPost) => composeCaption(post.caption, post.hashtags);

/** "Carrousel", "Reel": a post's format in the reader's language. */
export const typeLabel = (type: PostType) => i18n.t(`postType.${type}`);

/**
 * What a row is called: the caption; for a story (it has none) the day it goes
 * up, "Story du mar. 16 mars 2027" (A10-01), or "Story" before it has one;
 * else a placeholder.
 */
export function postTitle(post: ScheduledPost, storyLabel?: string): { text: string; isPlaceholder: boolean } {
  const caption = captionOf(post);
  if (caption) return { text: caption, isPlaceholder: false };
  return post.type === 'story'
    ? { text: storyLabel ?? i18n.t('posts.storyFallback'), isPlaceholder: false }
    : { text: i18n.t('posts.noCaption'), isPlaceholder: true };
}

/**
 * A story's title from its day, on the workspace's clock (D-146): a planned
 * day always carries its year ("Story du mar. 16 mars 2027"), a past one reads
 * short ("Story du 23 oct."); undefined when it has no date yet.
 */
export function useStoryLabel(): (post: ScheduledPost, zone: string) => string | undefined {
  const { t } = useTranslation();
  return (post, zone) => {
    if (post.type !== 'story') return undefined;
    if (post.publishedAt) return t('posts.storyTitle', { date: formatShortDate(post.publishedAt) });
    const parts = post.scheduledAt ? formatScheduled(post.scheduledAt, { timeZone: zone }) : null;
    return parts ? t('posts.storyTitle', { date: parts.date }) : undefined;
  };
}

/** The glossary status a post shows (GLOSSARY-FR §2); null for the few seconds it is being published. */
export function postStatus(post: ScheduledPost): Status | null {
  switch (post.status) {
    case 'canceled':
      return 'cancelled';
    case 'publishing':
      return null;
    default:
      return post.status;
  }
}

/** What the row's menu is called for screen readers: the caption, else the date, else the id. */
function rowName(post: ScheduledPost): string {
  const when = post.publishedAt ?? post.scheduledAt;
  return captionOf(post).slice(0, 24) || (when ? formatShortDate(when) : post.id);
}

/**
 * The first photo or video of a post. `md` is the list's stamp: 44px, a 9:16
 * tile for a story, and a Reel shows its cover or a labelled "Reel" tile (R14:
 * Reels without a cover looked all alike). `sm` is a campaign row's 32px stamp.
 */
export function PostThumb({ post, size = 'md' }: { post: ScheduledPost; size?: 'sm' | 'md' }) {
  const { t } = useTranslation();
  const isSmall = size === 'sm';
  if (isSmall) {
    const first = post.items[0];
    return (
      <span className="relative flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-(--color-accent-wash) text-(--color-accent)">
        {first ? <MediaThumb asset={first.asset} className="size-full" /> : <Icon name={TYPE_ICONS[post.type]} className="size-4" />}
      </span>
    );
  }
  const isReel = post.type === 'reel';
  // A Reel's cover, else its video's first frame, as before the redesign.
  const media = (isReel ? post.cover : null) ?? post.items[0]?.asset ?? null;
  const shape = post.type === 'story' ? 'h-14 w-9' : 'size-11';
  const tile = (
    <span className={`flex shrink-0 flex-col items-center justify-center gap-0.5 rounded-ds-sm bg-surface-3 text-ink-3 ${shape}`}>
      <Icon name={TYPE_ICONS[post.type]} className="size-4" />
      {isReel ? <span className="text-[9px] font-bold leading-none">{typeLabel('reel')}</span> : null}
    </span>
  );
  if (!media) return tile;
  return (
    <span className={`relative flex shrink-0 overflow-hidden rounded-ds-sm bg-surface-3 ${shape}`}>
      {/* A video that shows no frame (gone from storage, a codec the browser lacks) leaves the labelled tile visible (R14). */}
      {media.kind === 'video' ? <span className="absolute inset-0 flex">{tile}</span> : null}
      <MediaThumb asset={media} className="relative size-full" />
      {post.items.length > 1 ? (
        <span className="tabular absolute bottom-0.5 right-0.5 rounded-[4px] bg-(--color-ink-900)/80 px-1 text-[10px] font-bold leading-[14px] text-white">
          <span className="sr-only">, </span>
          {post.items.length}
          <span className="sr-only"> {t('posts.items', { count: post.items.length })}</span>
        </span>
      ) : null}
    </span>
  );
}

/** A chip is as wide as its words, up to the whole line: it truncates only when the line is full (A08-05). */
const CHIP = 'inline-flex h-5 max-w-full items-center gap-1 rounded-full px-2 text-ds-xs font-semibold';
const NEUTRAL_CHIP = `${CHIP} bg-surface-3 text-ink-2`;
const ACCENT_CHIP = `${CHIP} bg-primary-50 text-primary-700`;

/** "f Aussi sur ChatFlow", with the copy's state when it is not the post's. */
function CopyChip({ post, copy }: { post: ScheduledPost; copy: ScheduledPost }) {
  const { t } = useTranslation();
  const state = copy.status === post.status ? null : postStatus(copy);
  const platform = platformOf(copy.channelAccount.channelCode);
  return (
    <span className={NEUTRAL_CHIP}>
      <ChannelGlyph channel={platform === 'facebook' ? 'facebook' : 'instagram'} size={14} />
      <span className="truncate">{t('posts.alsoOn', { name: copy.channelAccount.displayName })}</span>
      {state ? <span className={state === 'failed' ? 'text-(--color-danger-text)' : 'text-ink-3'}>· {t(`status.${state}`)}</span> : null}
    </span>
  );
}

/** "Campagne · Collection Autumn": the list's chip, also on the phone agenda's rows. */
export function CampaignChip({ name }: { name: string }) {
  const { t } = useTranslation();
  return (
    <span className={NEUTRAL_CHIP}>
      <span className="truncate">{t('posts.campaignChip', { name })}</span>
    </span>
  );
}

/**
 * The campaign it belongs to (neutral) and the automations that answer its
 * comments (lilac, with the bolt): colour and words both tell them apart (R14).
 */
function PostChips({ post, copy }: { post: ScheduledPost; copy: ScheduledPost | null }) {
  const { t } = useTranslation();
  const shown = post.automations.slice(0, AUTOMATION_CHIPS);
  const more = post.automations.length - shown.length;
  return (
    <>
      {copy ? <CopyChip post={post} copy={copy} /> : null}
      {post.campaign ? <CampaignChip name={post.campaign.name} /> : null}
      {shown.map((a) => {
        const on = a.status === 'active';
        return (
          <span
            key={a.id}
            title={on ? t('posts.automationAnswers', { name: a.name }) : t('posts.automationOff', { name: a.name })}
            className={on ? ACCENT_CHIP : NEUTRAL_CHIP}
          >
            <Icon name="zap" className="size-3 shrink-0" />
            <span className="truncate">{a.name}</span>
            {on ? null : <span className="shrink-0"> {t('posts.off')}</span>}
          </span>
        );
      })}
      {more > 0 ? (
        <span title={post.automations.slice(AUTOMATION_CHIPS).map((a) => a.name).join(', ')} className="text-ds-xs font-semibold text-ink-3">
          +{more}
        </span>
      ) : null}
    </>
  );
}

/** Under the title: the format and the account (a story's is its account alone), then a planned time, then the chips. */
/** The account only when the list spans several: with one, every row would repeat it. */
function metaOf(post: ScheduledPost, tab: Tab, zone: string, showAccount: boolean): string {
  const account = showAccount ? post.channelAccount.displayName : null;
  if (tab === 'published') {
    const when = post.publishedAt ? formatWhen(post.publishedAt) : null;
    return post.type === 'story' ? (account ?? '') : [typeLabel(post.type), when].filter(Boolean).join(' · ');
  }
  const planned =
    tab === 'drafts' && post.scheduledAt
      ? i18n.t('posts.plannedFor', { when: formatWhen(post.scheduledAt, { scheduled: true, timeZone: zone }) })
      : null;
  return [post.type === 'story' ? null : typeLabel(post.type), account, planned].filter(Boolean).join(' · ');
}

/** On a published post a `lastError` is a warning (the id was not recorded), not a failure. */
function Notices({ post, copy }: { post: ScheduledPost; copy: ScheduledPost | null }) {
  const { t } = useTranslation();
  const insights = post.insights;
  const tone = (p: ScheduledPost) => (p.status === 'published' ? 'text-(--color-caution-text)' : 'text-(--color-danger-text)');
  return (
    <>
      {post.lastError ? <p className={`mt-1 text-xs ${tone(post)}`}>{post.lastError}</p> : null}
      {copy?.lastError ? (
        <p className={`mt-1 text-xs ${tone(copy)}`}>{t('posts.onPage', { page: copy.channelAccount.displayName, error: copy.lastError })}</p>
      ) : null}
      {post.status === 'published' && insights ? (
        insights.error ? (
          <InsightsNotice error={insights.error} post={post} />
        ) : insights.metrics ? null : (
          <p className="mt-1 text-xs text-ink-3">{t('insights.pending')}</p>
        )
      ) : null}
    </>
  );
}

/** One row of the list: the post, its copy riding on it, its menu, where it opens, and its retry. */
export interface PostRow {
  post: ScheduledPost;
  /** Its copy on a Page (D-143), shown on the same row. */
  copy: ScheduledPost | null;
  actions: MenuAction[];
  /** What the phone's slide shows (D-196): the quick ones of `actions`. */
  swipe: SwipeAction[];
  /** The composer, or the read-only page once published; null when it cannot be opened. */
  to: string | null;
  /** Only for a real failure that can go out again (A08-01). */
  onRetry: (() => void) | null;
}

/** The item: thumbnail, title, meta and chips, opening the post; notices under it, outside the link. */
function ContentCell({ row, tab, zone, showAccount }: { row: PostRow; tab: Tab; zone: string; showAccount: boolean }) {
  const storyLabel = useStoryLabel();
  const { post, copy, to } = row;
  const title = postTitle(post, storyLabel(post, zone));
  const meta = metaOf(post, tab, zone, showAccount);
  const body = (
    <>
      <PostThumb post={post} />
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className={`truncate font-semibold ${title.isPlaceholder ? 'text-ink-3' : 'text-ink'}`}>{title.text}</span>
        <span className="flex flex-wrap items-center gap-1.5 text-ds-xs text-ink-3">
          {meta ? <span className="whitespace-nowrap">{meta}</span> : null}
          <PostChips post={post} copy={copy} />
        </span>
      </span>
    </>
  );
  const indent = post.type === 'story' ? 'pl-12' : 'pl-14';
  return (
    <div className="min-w-0">
      {to ? (
        <Link to={to} className="-m-1 flex min-w-0 items-center gap-3 rounded-ds-md p-1 focus-visible:shadow-ring focus-visible:outline-none">
          {body}
        </Link>
      ) : (
        <div className="flex min-w-0 items-center gap-3">{body}</div>
      )}
      {/* Failed rows say why in their own column. */}
      {tab === 'failed' ? null : (
        <div className={indent}>
          <Notices post={post} copy={copy} />
        </div>
      )}
    </div>
  );
}

/** "Publishing…" keeps its chip: it is not a glossary status, only the few seconds before Published. */
function RowStatus({ post }: { post: ScheduledPost }) {
  const { t } = useTranslation();
  const status = postStatus(post);
  return status ? <StatusBadge status={status} /> : <Chip tone="caution">{t('posts.publishing')}</Chip>;
}

/** Failed or cancelled (A08-01): the reason, and Retry only when the post can go out again. */
function ReasonCell({ row, retrying }: { row: PostRow; retrying: boolean }) {
  const { t } = useTranslation();
  const { post, copy } = row;
  return (
    <div className="flex min-w-0 flex-col items-start gap-1.5">
      {post.status === 'canceled' ? <p className="text-ds-sm text-ink-2">{t('posts.cancelledNote')}</p> : null}
      {post.status !== 'canceled' && (post.lastError || copy?.lastError) ? (
        <div className="text-ds-sm">
          <Notices post={post} copy={copy} />
        </div>
      ) : null}
      {row.onRetry ? (
        <Button variant="secondary" size="sm" onClick={row.onRetry} disabled={retrying}>
          {t('posts.retry')}
        </Button>
      ) : null}
    </div>
  );
}

type MetricKey = 'views' | 'reach' | 'likes' | 'comments' | 'saved' | 'shares';
type MetricColumn = Exclude<MetricKey, 'comments'> | 'comments' | 'replies';

const POST_METRICS: ReadonlyArray<{ key: MetricKey; column: MetricColumn }> = [
  { key: 'views', column: 'views' },
  { key: 'reach', column: 'reach' },
  { key: 'likes', column: 'likes' },
  { key: 'comments', column: 'comments' },
  { key: 'saved', column: 'saved' },
];

/** A story has no likes or saves; Instagram reports its replies where a post has comments (D-136). */
const STORY_METRICS: ReadonlyArray<{ key: MetricKey; column: MetricColumn }> = [
  { key: 'views', column: 'views' },
  { key: 'reach', column: 'reach' },
  { key: 'comments', column: 'replies' },
  { key: 'shares', column: 'shares' },
];

/** A post's number, its copy's added in (D-143); null only when neither reported it. */
function metric(post: ScheduledPost, key: MetricKey, copy: ScheduledPost | null): number | null {
  const own = post.insights?.metrics?.[key] ?? null;
  const copied = copy?.insights?.metrics?.[key] ?? null;
  return own === null && copied === null ? null : (own ?? 0) + (copied ?? 0);
}

/** "—" (nothing reported) and "0" (measured zero) are both muted (GLOSSARY §3); the footer says which is which. */
function MetricValue({ value }: { value: number | null }) {
  const muted = value === null || value === 0;
  return <span className={muted ? 'text-ink-3' : 'font-semibold text-ink'}>{value === null ? '—' : formatNumber(value)}</span>;
}

/** The most recent sync across the list: shown once, beside the tabs. */
export function latestSync(posts: ScheduledPost[]): string | null {
  return posts.reduce<string | null>((latest, p) => {
    const at = p.insights?.syncedAt ?? null;
    return at && (!latest || at > latest) ? at : latest;
  }, null);
}

/** The columns of one tab of one list. */
export function usePostColumns({
  kind,
  tab,
  zone,
  showAccount,
  menuFor,
  onMenuFor,
  retrying,
}: {
  kind: ListKind;
  tab: Tab;
  /** The workspace's scheduling zone (D-146): planned dates and story titles read in it. */
  zone: string;
  /** The posts span several accounts, so each row names its own. */
  showAccount: boolean;
  menuFor: string | null;
  onMenuFor: (id: string | null) => void;
  retrying: boolean;
}): DataColumn<PostRow>[] {
  const { t } = useTranslation();
  const content: DataColumn<PostRow> = {
    key: 'content',
    header: t(`posts.kind.${kind}.noun`),
    cell: (row) => <ContentCell row={row} tab={tab} zone={zone} showAccount={showAccount} />,
  };
  // Last, labelled for screen readers by the row it acts on.
  const menu: DataColumn<PostRow> = {
    key: 'actions',
    header: t('posts.columns.actions'),
    hideHeader: true,
    width: 'w-14',
    narrow: 'end',
    cell: (row) => (
      <RowMenu
        isOpen={menuFor === row.post.id}
        onToggle={(open) => onMenuFor(open ? row.post.id : null)}
        actions={row.actions}
        label={t('posts.rowActions', { name: rowName(row.post) })}
      />
    ),
  };

  if (tab === 'published') {
    const metrics = kind === 'story' ? STORY_METRICS : POST_METRICS;
    return [
      content,
      ...metrics.map(
        (m, index): DataColumn<PostRow> => ({
          key: m.key,
          header: t(`posts.columns.${m.column}`),
          align: 'end',
          width: 'w-[104px]',
          cell: (row) => <MetricValue value={metric(row.post, m.key, row.copy)} />,
          // Narrow: views and likes (replies, for a story) on one line, labelled.
          narrow: index === 0 ? 'below' : false,
          narrowCell: (row) => <NarrowNumbers row={row} kind={kind} />,
        }),
      ),
      menu,
    ];
  }

  const status: DataColumn<PostRow> = {
    key: 'status',
    header: t('posts.columns.status'),
    width: 'w-[150px]',
    cell: (row) => <RowStatus post={row.post} />,
  };
  const third: Record<Exclude<Tab, 'published'>, DataColumn<PostRow>> = {
    upcoming: {
      key: 'goesLive',
      header: t('posts.columns.goesLive'),
      width: 'w-[210px]',
      cell: (row) => <GoesLive post={row.post} zone={zone} />,
      narrowCell: (row) => <GoesLive post={row.post} zone={zone} inline />,
    },
    drafts: {
      key: 'edited',
      header: t('posts.columns.edited'),
      width: 'w-[180px]',
      cell: (row) => <span className="text-ink-2">{formatWhen(row.post.updatedAt)}</span>,
      narrowCell: (row) => <span className="text-ink-3">{t('posts.edited', { when: formatWhen(row.post.updatedAt) })}</span>,
    },
    failed: {
      key: 'reason',
      header: t('posts.columns.reason'),
      width: 'w-[38%]',
      cell: (row) => <ReasonCell row={row} retrying={retrying} />,
    },
  };
  return [content, status, third[tab], menu];
}

/** "lun. 15 mars 2027" over "10:00 · Europe/Paris" (A00-06); on one line in a narrow list. */
function GoesLive({ post, zone, inline = false }: { post: ScheduledPost; zone: string; inline?: boolean }) {
  const at = post.scheduledAt ? formatScheduled(post.scheduledAt, { timeZone: zone }) : null;
  if (!at) return null;
  if (inline) return <span className="text-ink-2">{`${at.date} · ${at.time} · ${at.zone}`}</span>;
  return (
    <span className="flex flex-col">
      <span className="font-semibold text-ink">{at.date}</span>
      <span className="text-ds-sm text-ink-3">
        {at.time} · {at.zone}
      </span>
    </span>
  );
}

/** "1 234 vues · 54 j’aime": a narrow list's numbers (replies, for a story), on a line of their own. */
function NarrowNumbers({ row, kind }: { row: PostRow; kind: ListKind }) {
  const { t } = useTranslation();
  const views = metric(row.post, 'views', row.copy);
  const second = kind === 'story' ? metric(row.post, 'comments', row.copy) : metric(row.post, 'likes', row.copy);
  const parts = [
    views === null ? null : t('posts.narrow.views', { count: views, value: formatNumber(views) }),
    second === null ? null : t(kind === 'story' ? 'posts.narrow.replies' : 'posts.narrow.likes', { count: second, value: formatNumber(second) }),
  ].filter(Boolean);
  return parts.length > 0 ? <span className="tabular text-ds-sm font-semibold text-ink-2">{parts.join(' · ')}</span> : null;
}
